import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Layers, ListChecks, Printer, Package, AlertCircle, Clock } from 'lucide-react';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [recentModels, setRecentModels] = useState([]);
  const [activeQueue, setActiveQueue] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/models'),
      api.get('/queue'),
      api.get('/machines'),
      api.get('/filaments'),
      api.get('/models?smart_filter=needs_gcode'),
    ]).then(([models, queue, machines, filaments, needsGcode]) => {
      setStats({
        totalModels: models.data.models.length,
        activeJobs: queue.data.queue.length,
        machines: machines.data.machines.length,
        filaments: filaments.data.filaments.length,
        needsGcode: needsGcode.data.models.length,
      });
      setRecentModels(models.data.models.slice(0, 6));
      setActiveQueue(queue.data.queue.slice(0, 5));
    });
  }, []);

  if (!stats) return <div style={{ color: 'var(--text-muted)', padding: '40px', textAlign: 'center' }}>Loading dashboard...</div>;

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Dashboard</h1>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Total Models</div>
          <div className="stat-value" style={{ color: 'var(--accent)' }}>{stats.totalModels}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Prints</div>
          <div className="stat-value" style={{ color: 'var(--warning)' }}>{stats.activeJobs}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Machines</div>
          <div className="stat-value">{stats.machines}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Filament Spools</div>
          <div className="stat-value">{stats.filaments}</div>
        </div>
      </div>

      {stats.needsGcode > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'var(--warning-dim)', padding: '14px 18px', borderRadius: 'var(--radius)', marginBottom: '24px', fontSize: '14px', color: 'var(--warning)' }}>
          <AlertCircle size={18} />
          {stats.needsGcode} model{stats.needsGcode > 1 ? 's' : ''} missing G-code profiles
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div>
          <h3 style={{ marginBottom: '14px', fontSize: '16px', fontWeight: '600' }}>Recent Models</h3>
          {recentModels.length === 0 ? (
            <div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>
              No models yet. Click "Models" to add your first one.
            </div>
          ) : (
            recentModels.map(m => (
              <div key={m.id} className="part-row" style={{ cursor: 'pointer' }} onClick={() => navigate(`/models/${m.id}`)}>
                <Layers size={18} color="var(--accent)" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500' }}>{m.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{m.part_count} part{m.part_count !== 1 ? 's' : ''}</div>
                </div>
                {m.tag_names && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {m.tag_names.split(',').slice(0, 2).map(t => <span key={t} className="tag">{t}</span>)}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <div>
          <h3 style={{ marginBottom: '14px', fontSize: '16px', fontWeight: '600' }}>Active Print Queue</h3>
          {activeQueue.length === 0 ? (
            <div className="card card-body" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>
              No active prints. Add a model to the print queue.
            </div>
          ) : (
            activeQueue.map(q => (
              <div key={q.id} className="queue-item" onClick={() => navigate('/queue')} style={{ cursor: 'pointer' }}>
                <div className="queue-item-header">
                  <span className="queue-item-title">{q.model_name}</span>
                  <span className="badge badge-accent">{q.target_quantity}x</span>
                </div>
                <div className="progress-bar">
                  <div className={`progress-bar-fill ${q.overall_progress === 100 ? 'complete' : ''}`} style={{ width: `${q.overall_progress}%` }} />
                </div>
                <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>{q.overall_progress}% complete</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
